import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { FilterChips } from '~/components/FilterChips';
import { Table } from '~/components/Table';
import { usePagination } from '~/hooks/usePagination';
import { useUserList } from '~/modules/settings/api/queries/userList';
import { FilterDrawer } from '~/modules/settings/components/FilterList';
import { DEFAULT_PAGE_SIZE } from '~/modules/settings/constants';
import { USER_TYPES } from '~/modules/settings/constants/Filter';
import { useFilters } from '~/modules/settings/hooks/useFilters';
import { useSearch } from '~/modules/settings/hooks/useSearch';
import { useSort } from '~/modules/settings/hooks/useSort';
import { UsersFilterForm } from '~/modules/settings/modules/user/components/UserFormFilter/UserFormFilter';
import type { UserListItem } from '~settings/api/lib/schemas';
import { HeaderSmall } from '~settings/components/HeaderSmall';
import { ListControls } from '~settings/components/ListControls';
import { SETTINGS_ROUTES } from '~settings/config/routes';
import { translateOptions } from '~settings/utils/helpers';

import { useUsersColumns } from './hooks/useUsersColumns';

const userEmptyArray: UserListItem[] = [];

interface UsersProps {
  isSublabel: boolean;
  labelId?: string;
  total: number;
}

export const Users = ({ isSublabel, labelId, total }: UsersProps) => {
  const { t } = useTranslation('settings');
  const columns = useUsersColumns(labelId);
  const navigate = useNavigate();

  const { sorting, setSorting, sortParams } = useSort();
  const { searchTerm } = useSearch();
  const { page, setPageNumber, perPage, skip, take } =
    usePagination(DEFAULT_PAGE_SIZE);
  const { filterData, drawerOpen, closeDrawer, openDrawer } = useFilters();

  const { userList, isFetching } = useUserList({
    skip,
    take,
    labelIds: labelId,
    ...sortParams,
    ...filterData,
    ...(searchTerm && { search: searchTerm }),
  });

  const onClick = (row: UserListItem) => {
    navigate(SETTINGS_ROUTES.USER_DETAIL(row.id));
  };

  return (
    <>
      {isSublabel ? (
        <HeaderSmall>
          {t('labelDetails.usersCount', {
            usersCount: total,
          })}
        </HeaderSmall>
      ) : null}
      <ListControls
        placeholder={t('user.search')}
        page={page}
        total={userList?.total ?? 0}
        perPage={perPage}
        entity="users"
        onFilterClick={openDrawer}
        internalFilter={
          <FilterChips
            options={translateOptions(USER_TYPES, t)}
            path="isInternal"
          />
        }
        setPageNumber={setPageNumber}
      />
      <Table
        data={userList?.data ?? userEmptyArray}
        columns={columns}
        rowClassName="odd:bg-level-03"
        onRowClick={onClick}
        sort={{
          sorting,
          onSortingChange: setSorting,
        }}
        skeletonLoading={{ isLoading: isFetching, prefillRowsNumber: 10 }}
      />
      <FilterDrawer open={drawerOpen} onOpenChange={closeDrawer}>
        <UsersFilterForm open={drawerOpen} onApply={closeDrawer} />
      </FilterDrawer>
    </>
  );
};

Users.displayName = 'LabelUsers';
